import { Fragment, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { kernsatz } from '@schnitt/shared';
import { api, type VersionInfo } from '../api';
import { Sheet } from './Sheet';

const SEEN = 'schnitt.seen';

interface Seen {
  /** The build this browser last acknowledged. */
  build: string;
  /** And the date of the newest entry it had at the time. */
  date: string;
}

/**
 * What changed since you last looked.
 *
 * The installation is updated every couple of hours, which is only useful if
 * somebody can see what each update did. Two things are remembered per
 * browser: which build was acknowledged -- that decides *whether* there is
 * something new -- and the newest entry seen at the time, which decides how
 * many entries that is.
 */
export function useNewSince(): { info: VersionInfo | null; unseen: number; markSeen: () => void } {
  const [info, setInfo] = useState<VersionInfo | null>(null);
  const [seen, setSeen] = useState<Seen | null>(() => read());

  useEffect(() => {
    void api
      .version()
      .then((v) => {
        setInfo(v);
        // Nothing to announce on a first visit: everything would be "new",
        // which tells a newcomer nothing at all.
        if (read() === null) {
          const fresh = { build: v.build, date: v.entries[0]?.date ?? '' };
          write(fresh);
          setSeen(fresh);
        }
      })
      .catch(() => setInfo(null));
  }, []);

  const unseen = info && seen && seen.build !== info.build ? countSince(info, seen.date) : 0;
  return {
    info,
    unseen,
    markSeen: () => {
      if (!info) return;
      const fresh = { build: info.build, date: info.entries[0]?.date ?? seen?.date ?? '' };
      write(fresh);
      setSeen(fresh);
    },
  };
}

/** Entries newer than the one last seen; at least one if the build moved. */
function countSince(info: VersionInfo, seenDate: string): number {
  if (!seenDate) return info.entries.length;
  const index = info.entries.findIndex((e) => e.date <= seenDate);
  return index < 0 ? info.entries.length : Math.max(index, 1);
}

function read(): Seen | null {
  try {
    const raw = localStorage.getItem(SEEN);
    return raw ? (JSON.parse(raw) as Seen) : null;
  } catch {
    // Private mode, storage switched off, or something else wrote nonsense
    // here. Then there is no memory of a previous visit and nothing is
    // announced, which is the right way to be wrong.
    return null;
  }
}

function write(value: Seen): void {
  try {
    localStorage.setItem(SEEN, JSON.stringify(value));
  } catch {
    /* see read() */
  }
}

export function ChangesSheet({
  info,
  istAdmin = false,
  onClose,
}: {
  info: VersionInfo | null;
  /** Nur Admins sehen den langen Text -- und auch die erst auf Wunsch. */
  istAdmin?: boolean;
  onClose: () => void;
}): ReactElement {
  /**
   * Release Notes, so wie Sascha sie am 18. September gezeigt hat.
   *
   * „Ab jetzt: Release notes nur noch so. Kein ‚was ist neu‘ mit einer
   * 10 zeiligen Überschrift.“ Auf dem Bildschirmfoto: die Fassung, ein
   * Dreiwortsatz darunter, und zwei kurze Listen -- *Neu* und *Fixes*, je
   * Zeile zwei bis vier Wörter. Nichts sonst.
   *
   * Vorher stand hier eine Bekanntmachung mit einer grossen Karte, einem
   * einleitenden Satz und zu jeder Überschrift einem Satz. Das war die
   * Antwort auf „wirkt so overloaded“ vom 7. September und war besser als
   * das Protokoll davor -- aber die Überschrift war eine Aufzählung von
   * fünfundzwanzig Funktionen geworden, und genau die ist gemeint.
   *
   * Ältere Ausgaben haben die Listen nicht. Sie liegen hinter dem Knopf, als
   * Zeilen mit Datum und Überschrift -- Geschichte wird nicht umgeschrieben.
   */
  const [offen, setOffen] = useState(false);
  const [alle, setAlle] = useState(false);

  const eintraege = info?.entries ?? [];
  const aktuell = eintraege[0] ?? null;
  const frueher = eintraege.slice(1);
  const punkte = (aktuell?.neu?.length ?? 0) + (aktuell?.fixes?.length ?? 0);
  const dahinter = aktuell?.dahinter?.trim() ?? '';

  return (
    <Sheet title="Release Notes" onClose={onClose}>
      {!info ? (
        <p className="hint">Die Änderungen konnten nicht geladen werden.</p>
      ) : !aktuell ? (
        <p className="hint">Noch nichts aufgeschrieben.</p>
      ) : (
        <>
          <div className="rn-kopf">
            <div className="rn-version" data-rn-version>
              {info.release ? `Version ${info.release}` : formatDate(aktuell.date)}
            </div>
            {/* Der Dreiwortsatz. Er steht im Änderungsbuch als Überschrift der
                Ausgabe -- bei älteren Ausgaben ist das noch die lange
                Aufzählung, und dann wird sie eben lang. */}
            <div className="rn-claim" data-rn-claim>{aktuell.title}</div>
          </div>

          {punkte > 0 ? (
            <>
              <Punkteliste name="Neu" zeichen="✦" punkte={aktuell.neu ?? []} marke="neu" />
              <Punkteliste name="Fixes" zeichen="✓" punkte={aktuell.fixes ?? []} marke="fixes" />
            </>
          ) : (
            // Eine Ausgabe von vor dem 18. September steht ganz oben: dann
            // gibt es keine Listen, und der eine Satz ist alles, was übrig
            // bleibt.
            <p className="rn-satz" data-rn-satz>{aktuell.satz || kernsatz(aktuell.body)}</p>
          )}

          {istAdmin && dahinter ? (
            <>
              <button
                className="ghost rn-warum"
                aria-expanded={offen}
                data-eintrag
                onClick={() => setOffen(!offen)}
              >
                {offen ? 'Weniger ▴' : 'Warum ▾'}
              </button>
              {offen ? <Langtext text={dahinter} ohne="" /> : null}
              <p className="hint">Das sehen nur Admins.</p>
            </>
          ) : null}

          {frueher.length > 0 ? (
            <button className="ghost neu-frueher" data-frueher onClick={() => setAlle(!alle)}>
              {alle ? 'Frühere Ausgaben ausblenden' : `Frühere Ausgaben (${frueher.length})`}
            </button>
          ) : null}
          {alle
            ? frueher.map((entry) => (
                <article className="rn-alt" data-rn-alt key={`${entry.date}-${entry.title}`}>
                  <span className="rn-alt-datum">
                    {entry.version ? <b data-rn-alt-version>{entry.version}</b> : null}
                    {entry.version ? ' · ' : ''}
                    {formatDate(entry.date)}
                  </span>
                  <span className="rn-alt-titel">{entry.title}</span>
                </article>
              ))
            : null}

          <p className="hint">
            Läuft seit {info.builtAt ? formatDateTime(info.builtAt) : 'unbekannt'}
            {/* Die genaue Fassung für den, der sie braucht -- die Nummer oben
                gilt für eine Ausgabe, und eine Ausgabe kann zwei Bauten
                umfassen (28. Oktober). */}
            {info.bau && info.bau !== info.release ? ` · Bau ${info.bau}` : ''}
            {info.commit ? ` · Stand ${info.commit.slice(0, 7)}` : ''}
          </p>
        </>
      )}
    </Sheet>
  );
}

/**
 * Eine der beiden Listen.
 *
 * Leer heisst: gar nicht da. Eine Überschrift „Fixes“ über nichts ist eine
 * Ankündigung, die sich nicht einlöst.
 */
function Punkteliste({
  name,
  zeichen,
  punkte,
  marke,
}: {
  name: string;
  zeichen: string;
  punkte: readonly string[];
  marke: string;
}): ReactElement | null {
  if (punkte.length === 0) return null;
  return (
    <section className="rn-block" data-rn-block={marke}>
      <h3 className="rn-block-titel">
        <span className="rn-block-zeichen" aria-hidden="true">{zeichen}</span>
        {name}
      </h3>
      <ul className="rn-liste">
        {punkte.map((punkt) => (
          // Beginnt die Zeile mit einem Emoji (3. Oktober), ist das ihr
          // Aufzählungszeichen -- dann ohne den Punkt davor.
          <li key={punkt} data-rn-punkt className={/^\p{Extended_Pictographic}/u.test(punkt) ? 'mit-emoji' : undefined}>
            {punkt}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Der lange Text darunter -- für Admins, auf Wunsch.
 *
 * Er kommt aus dem Änderungsbuch und ist dort in Markdown geschrieben. Roh
 * hingestellt standen die Sternchen mit im Bild (Sascha, 8. September, mit
 * Bildschirmfoto). Also wird gesetzt, was ein Änderungstext braucht und mehr
 * nicht: fett, kursiv, Code, Absätze. Kein Markdown-Übersetzer als Paket --
 * drei Zeichen sind keine Bibliothek wert, und was hier hineinkommt, schreibe
 * ich selbst.
 */
function Langtext({ text, ohne }: { text: string; ohne: string }): ReactElement {
  const absaetze = text
    .split(/\n\s*\n/)
    .map((a) => a.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean)
    // Der Satz, der oben schon steht, steht nicht noch einmal darunter.
    .map((a, i) => (i === 0 ? ohneVorspann(a, ohne) : a))
    .filter(Boolean);
  return (
    <div className="neu-langtext" data-langtext>
      {absaetze.map((absatz, i) => (
        <p key={i}>{gesetzt(absatz)}</p>
      ))}
    </div>
  );
}

/** Den Satz abziehen, der als Nachricht schon oben steht. */
function ohneVorspann(absatz: string, satz: string): string {
  if (!satz) return absatz;
  const roh = absatz.replace(/[*`]/g, '');
  if (!roh.startsWith(satz)) return absatz;
  const rest = roh.slice(satz.length).trim();
  return rest;
}

/** `**fett**`, `*kursiv*`, `` `Code` `` -- alles andere bleibt Text. */
function gesetzt(absatz: string): ReactNode[] {
  const teile = absatz.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`)/g);
  return teile.filter(Boolean).map((teil, i) => {
    if (teil.startsWith('**') && teil.endsWith('**')) return <strong key={i}>{teil.slice(2, -2)}</strong>;
    if (teil.startsWith('`') && teil.endsWith('`')) return <code key={i}>{teil.slice(1, -1)}</code>;
    if (teil.startsWith('*') && teil.endsWith('*') && teil.length > 2) return <em key={i}>{teil.slice(1, -1)}</em>;
    return <Fragment key={i}>{teil}</Fragment>;
  });
}

/**
 * Der eine Satz, der sagt, was sich geändert hat.
 *
 * Im Änderungsbuch beginnt jeder Absatz, der eine Neuerung ankündigt, fett --
 * „**Leere Zustände sagen jetzt etwas und haben einen Knopf.**“. Genau der
 * gehört hierher. Gibt es keinen, nimmt sie den ersten Satz des Textes; und
 * gibt es auch den nicht, bleibt es bei der Überschrift allein. Lieber keine
 * Zeile als eine abgeschnittene Begründung.
 *
 * Die Rechnung steht seit 7.84 im gemeinsamen Teil: der **Server** schickt den
 * Satz fertig mit, weil er den ganzen Text gar nicht mehr herausgibt. Hier
 * bleibt sie als Rückfall, falls eine ältere Antwort ohne `satz` ankommt --
 * und dieselbe Rechnung zweimal zu schreiben, war noch nie eine gute Idee.
 */
export { kernsatz };

function formatDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString('de-DE', { day: '2-digit', month: 'long' });
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}
